import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/layout/Layout";
import { validateImageFile } from "@/components/common/PhotoUploader";

const Card = ({ title, children }) => <section className="panel space-y-4 p-6"><h2 className="font-display text-lg font-semibold">{title}</h2>{children}</section>;

function Avatar() {
  const { user, refresh } = useAuth();
  const ref = useRef();
  const onFile = async (file) => {
    const bad = validateImageFile(file);
    if (bad) { toast.error(bad); return; }
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/users/me/avatar", fd); toast.success("Profil fotoğrafı güncellendi"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-4">
      {user.avatar_url ? <img src={fileUrl(user.avatar_url)} alt={user.full_name || "Kullanıcı profili"} loading="lazy" decoding="async" className="h-16 w-16 rounded-full object-cover" data-testid="settings-avatar" /> : <div className="grid h-16 w-16 place-items-center rounded-full bg-black/[0.05] text-xl">{user.full_name?.[0]}</div>}
      <Button variant="outline" className="bg-transparent" onClick={() => ref.current?.click()} data-testid="avatar-upload-button">Fotoğraf yükle</Button>
      <input ref={ref} type="file" hidden accept="image/jpeg,image/png,image/webp" data-testid="avatar-file-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </div>
  );
}

export default function Settings() {
  const { user, setUser } = useAuth();
  const [p, setP] = useState({ full_name: user.full_name, phone: user.phone || "", bio: user.bio || "", city: user.city || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", new_password_confirm: "" });
  const saveProfile = async () => {
    try { setUser((await api.patch("/auth/me", p)).data.user); toast.success("Profil kaydedildi"); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle2fa = async (enabled) => {
    try { await api.post("/auth/me/2fa", { enabled }); setUser({ ...user, two_factor_enabled: enabled }); toast.success(enabled ? "2 aşamalı doğrulama açıldı" : "2 aşamalı doğrulama kapatıldı"); } catch (e) { toast.error(errMsg(e)); }
  };
  const changePw = async () => {
    try { await api.post("/auth/me/password", pw); toast.success("Şifre güncellendi"); setPw({ current_password: "", new_password: "", new_password_confirm: "" }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Page className="max-w-3xl space-y-6">
      <h1 className="font-display text-4xl font-semibold">Hesap ayarları</h1>
      <Card title="Profil">
        <Avatar />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>İsim soyisim</Label><Input data-testid="settings-name-input" value={p.full_name} onChange={(e) => setP({ ...p, full_name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Telefon</Label><Input data-testid="settings-phone-input" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Şehir</Label><Input data-testid="settings-city-input" value={p.city} onChange={(e) => setP({ ...p, city: e.target.value })} /></div>
        </div>
        <Textarea data-testid="settings-bio-input" placeholder="Hakkımda" value={p.bio} onChange={(e) => setP({ ...p, bio: e.target.value })} />
        <Button onClick={saveProfile} data-testid="settings-save-profile" className="bg-[#0071e3] text-white">Kaydet</Button>
      </Card>
      <Card title="Güvenlik">
        <label className="flex items-center justify-between gap-4">
          <span><span className="font-medium">2 aşamalı doğrulama</span><span className="block text-sm text-zinc-500">Açıkken her girişte e-postanıza gelen kod istenir.</span></span>
          <Switch data-testid="settings-2fa-switch" checked={!!user.two_factor_enabled} onCheckedChange={toggle2fa} />
        </label>
        <div className="grid gap-3 sm:grid-cols-3">
          {user.auth_provider !== "google" && <Input data-testid="settings-current-password" type="password" placeholder="Mevcut şifre" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />}
          <Input data-testid="settings-new-password" type="password" placeholder="Yeni şifre" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          <Input data-testid="settings-new-password-confirm" type="password" placeholder="Yeni şifre tekrar" value={pw.new_password_confirm} onChange={(e) => setPw({ ...pw, new_password_confirm: e.target.value })} />
        </div>
        <Button variant="outline" className="bg-transparent" onClick={changePw} data-testid="settings-change-password">Şifreyi değiştir</Button>
      </Card>
    </Page>
  );
}
